import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ReloginContext } from "../ReloginContext";
import {
  adminDeleteSurveyProject,
  createSurveyEndpoint,
  deleteSurveyEndpoint,
  getCostSheetImportUrl,
  getOrgSurveys,
  getSurveyEndpoints,
  type ImportCostSheetResponse,
  importCostSheet as importCostSheetHttp,
  type SurveyLanguage,
  updateProjectSite,
  updateSurveyEndpointLanguage,
  updateSurveyEndpointOrg,
} from "../http/surveyEndpoints";
import putSiteFile from "../http/putSiteFile";
import { getAuthTokenBlocking } from "./utils";

const XLSX_CONTENT_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

interface CreateEndpointVariables {
  name: string;
  orgId: string;
}

interface UpdateProjectSiteVariables {
  endpointId: string;
  projectId: string;
  siteId: string | null;
}

interface UpdateEndpointOrgVariables {
  endpointId: string;
  orgId: string | null;
}

interface UpdateEndpointLanguageVariables {
  endpointId: string;
  defaultLanguage: SurveyLanguage | null;
}

interface AdminDeleteProjectVariables {
  endpointId: string;
  projectId: string;
}

interface ImportCostSheetVariables {
  endpointId: string;
  projectId: string;
  file: File;
}

const useSurveyEndpoints = (orgId?: string) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  const queryClient = useQueryClient();

  const fetchSurveyEndpoints = useQuery(
    ["survey-endpoints"],
    async () =>
      getSurveyEndpoints(await getAuthTokenBlocking(getToken, relogin))
  );

  const fetchOrgSurveys = useQuery(
    ["survey-endpoints", "org", orgId],
    async () =>
      getOrgSurveys(await getAuthTokenBlocking(getToken, relogin), orgId!),
    { enabled: !!orgId }
  );

  const invalidateAll = () => {
    queryClient.invalidateQueries(["survey-endpoints"]);
  };

  const createEndpoint = useMutation(
    async ({ name, orgId: epcOrgId }: CreateEndpointVariables) =>
      createSurveyEndpoint(
        await getAuthTokenBlocking(getToken, relogin),
        name,
        epcOrgId
      ),
    { onSuccess: invalidateAll }
  );

  const deleteEndpoint = useMutation(
    async (id: string) =>
      deleteSurveyEndpoint(
        await getAuthTokenBlocking(getToken, relogin),
        id
      ),
    { onSuccess: invalidateAll }
  );

  const updateSiteOnProject = useMutation(
    async ({
      endpointId,
      projectId,
      siteId,
    }: UpdateProjectSiteVariables) =>
      updateProjectSite(
        await getAuthTokenBlocking(getToken, relogin),
        endpointId,
        projectId,
        siteId
      ),
    { onSuccess: invalidateAll }
  );

  const updateEndpointOrg = useMutation(
    async ({ endpointId, orgId: nextOrgId }: UpdateEndpointOrgVariables) =>
      updateSurveyEndpointOrg(
        await getAuthTokenBlocking(getToken, relogin),
        endpointId,
        nextOrgId
      ),
    { onSuccess: invalidateAll }
  );

  const updateEndpointLanguage = useMutation(
    async ({
      endpointId,
      defaultLanguage,
    }: UpdateEndpointLanguageVariables) =>
      updateSurveyEndpointLanguage(
        await getAuthTokenBlocking(getToken, relogin),
        endpointId,
        defaultLanguage
      ),
    { onSuccess: invalidateAll }
  );

  const adminDeleteProject = useMutation(
    async ({ endpointId, projectId }: AdminDeleteProjectVariables) =>
      adminDeleteSurveyProject(
        await getAuthTokenBlocking(getToken, relogin),
        endpointId,
        projectId
      ),
    { onSuccess: invalidateAll }
  );

  const importCostSheet = useMutation<
    ImportCostSheetResponse,
    unknown,
    ImportCostSheetVariables
  >(
    async ({ endpointId, projectId, file }: ImportCostSheetVariables) => {
      const authToken = await getAuthTokenBlocking(getToken, relogin);
      const { url, s3Key } = await getCostSheetImportUrl(
        authToken,
        endpointId,
        projectId,
        file.name,
        XLSX_CONTENT_TYPE
      );
      await putSiteFile(url, XLSX_CONTENT_TYPE, file);
      return importCostSheetHttp(
        authToken,
        endpointId,
        projectId,
        s3Key,
        file.name
      );
    },
    {
      // A successful parse writes a new version + replaces the form answers;
      // refresh the surveys table either way so the badge / version reflects it.
      onSuccess: invalidateAll,
    }
  );

  return {
    fetchSurveyEndpoints,
    fetchOrgSurveys,
    createEndpoint,
    deleteEndpoint,
    updateSiteOnProject,
    updateEndpointOrg,
    updateEndpointLanguage,
    adminDeleteProject,
    importCostSheet,
  };
};

export default useSurveyEndpoints;
